import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { ContactUsMessageDetails } from 'src/app/core/data/model/contact-us-message-details-model';
import { ContactUsMessages } from 'src/app/core/data/model/contact-us-messages-model';
import { BlocState, ReadyState, ShowSuccessMessageState } from 'src/app/shared/bloc/base-bloc';
import { IconsConstants, SharedConstants } from 'src/app/shared/common';
import { BasePopUpComponent } from 'src/app/shared/component/base-popup-component/base-popup.component';
import { CustomFormControlComponent } from 'src/app/shared/component/custom-form-control/custom-form-control.component';
import { ErrorValidationMessageComponent } from 'src/app/shared/component/error-validation-message/error-validation-message.component';
import { FileUploadControlComponent } from 'src/app/shared/component/file-upload-control/file-upload-control.component';
import { DateFormatterPipe } from 'src/app/shared/pipes';
import {
  ContactUsPopUpBloc,
  ContactUsPopUpInitEvent,
  ContactUsPopUpSendReplyEvent,
} from '../../../contact-us-messages/bloc/contact-us-popup-bloc';
import { FileType } from 'src/app/core/common';
import { InputFieldComponent } from 'src/app/shared/component/input-field/input-field.component';
import { ButtonCaller, ButtonSharedComponent } from 'src/app/shared/component/shared-button-component/shared-button.component';
import { IconSvgComponent } from 'src/app/shared/component/icon/svg-icon.component';
import { TimeFormatterPipe } from 'src/app/shared/pipes/time-formatter.pipe';

@Component({
  selector: 'app-contact-us-popup',
  standalone: true,
  imports: [
    CommonModule,
    CustomFormControlComponent,
    FileUploadControlComponent,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatFormFieldModule,
    ErrorValidationMessageComponent,
    MatInputModule,
    InputFieldComponent,
    ButtonSharedComponent,
    IconSvgComponent,
    DateFormatterPipe,
    TimeFormatterPipe
],
  templateUrl: './contact-us-popup.component.html',
  styleUrls: ['./contact-us-popup.component.scss'],
  providers: [ContactUsPopUpBloc],
})
export class ContactUsPopupComponent
  extends BasePopUpComponent<ContactUsMessageDetails[]>
  implements OnInit, OnChanges, AfterViewChecked
{
  form!: FormGroup;
  date = new Date();
  messagesDetails: ContactUsMessageDetails[] = [];
  showNewReply: boolean = false;
  private shouldScroll: boolean = false;

  @Input() message: ContactUsMessages;
  @ViewChild('popupContainer') popupContainer!: ElementRef;

  // enums
  FileType = FileType;

  //constants
  IconsConstants = IconsConstants;
  SharedConstants = SharedConstants;

  constructor(injector: Injector, private bloc: ContactUsPopUpBloc) {
    super([], injector);
    this.bloc.onState.pipe(takeUntil(this.destroy$)).subscribe((state) => this.onHandlePopupState(state));
  }

  //hooks
  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['initializeCaller'].currentValue) {
      this.initializePopupData();
    }
  }

  ngAfterViewChecked(): void {
    this.onScrollToBottom();
  }

  //public
  onSubmit(): ButtonCaller {
    return () => this.bloc.setEvent.next(new ContactUsPopUpSendReplyEvent(this.form.value));
  }

  onShowNewReplySection(): ButtonCaller {
    return () => {
      this.showNewReply = true;
      this.shouldScroll = true; 
    }
  }

  //override
  override onHandlePopupState(state: BlocState): void {
    // call the base method
    super.onHandlePopupState(state);
    if (state instanceof ReadyState) {
      this.messagesDetails = state.data;
    } else if (state instanceof ShowSuccessMessageState) {
      this.afterClosePopupObject = {isMessageHasReply : true}
      this.setAfterCloseDialogValue();
      this.matDialogRef.close(this.afterClosePopupObject);
    }
  }

  // private
  private initializeForm(): void {
    this.form = new FormGroup({
      appNotify: new FormControl(),
      emailNotify: new FormControl(),
      message: new FormControl(),
      id: new FormControl(this.message.id),
      file: new FormControl([]),
      title: new FormControl({value: this.message?.title, disabled: true}),
      messageContent: new FormControl({value: this.message?.message, disabled: true}),
    });
  }

  private initializePopupData(): void {
    this.bloc.setEvent.next(new ContactUsPopUpInitEvent(this.initializeCaller));
  }

  private onScrollToBottom(): void {
    if(this.shouldScroll) {
      this.shouldScroll = false;
      const element = this.popupContainer.nativeElement;
      element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    }
  }
}
